import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import { Navigate, useNavigate } from "react-router-dom"
import { FormLayout } from "../components/layouts"
import {
  IEstimates,
  IQuestionValues,
  QuestionFields,
  pickQuestionValues,
} from "../features/bpo"
import { IContactValues, pickContactValues } from "../features/contact"
import { IHSField, OBJECT_TYPE_ID_MAP, sendForm } from "../features/hubspot"
import { callAPI } from "../utils"

type IFormValues = IQuestionValues & IContactValues

const toHSFields = (
  questionValues: IQuestionValues,
  estimates: IEstimates
): IHSField[] => {
  const fields = [
    // inputs
    {
      objectTypeId: OBJECT_TYPE_ID_MAP["contact"],
      name: "simulator_bpo_sow",
      value: questionValues.scope_of_work.join(";"),
    },
    {
      objectTypeId: OBJECT_TYPE_ID_MAP["contact"],
      name: "simulator_bpo_num_apps",
      value: questionValues.num_apps,
    },
    {
      objectTypeId: OBJECT_TYPE_ID_MAP["contact"],
      name: "simulator_bpo_num_joins",
      value: questionValues.num_joins,
    },
    {
      objectTypeId: OBJECT_TYPE_ID_MAP["contact"],
      name: "simulator_bpo_num_leaves",
      value: questionValues.num_leaves,
    },
    {
      objectTypeId: OBJECT_TYPE_ID_MAP["contact"],
      name: "simulator_bpo_num_transfers",
      value: questionValues.num_transfers,
    },
    // estimates
    {
      objectTypeId: OBJECT_TYPE_ID_MAP["contact"],
      name: "simulator_bpo_monthly_estimate",
      value: estimates.monthly_estimate,
    },
    {
      objectTypeId: OBJECT_TYPE_ID_MAP["contact"],
      name: "simulator_bpo_yearly_estimate",
      value: estimates.yearly_estimate,
    },
    {
      objectTypeId: OBJECT_TYPE_ID_MAP["contact"],
      name: "simulator_bpo_fulltime_equivalent",
      value: estimates.fulltime_equivalent,
    },
  ]

  return fields
}

const BPO = () => {
  const methods = useForm<IFormValues>()
  const navigate = useNavigate()

  const onSubmit: SubmitHandler<IFormValues> = async (formValues) => {
    // NOTE: errors not tied to a field will not be automatically cleared
    methods.clearErrors()

    const contactValues = pickContactValues(formValues)
    const questionValues = pickQuestionValues(formValues)
    const { data, errorType } = await callAPI<IQuestionValues, IEstimates>(
      process.env.REACT_APP_BPO_ENDPOINT,
      formValues
    )

    if (data) {
      // success
      const HSQuestionFields = toHSFields(questionValues, data)
      await sendForm("bpo", contactValues, HSQuestionFields)
      navigate("result", { state: { questionValues, simulationResults: data } })
    }

    if (errorType && errorType === "server") {
      // server side error, so nothing user can do
      return <Navigate to="/" />
    }

    if (errorType && errorType === "invalidEmail") {
      methods.setError("email", {
        type: "custom",
        message: "勤務先のメールアドレスを入力してください",
      })
    }

    if (errorType && errorType === "client") {
      // client side error
      methods.setError("root.apiError", {
        type: "client",
        message:
          "フォームに不正な値が入力されているため、見積もりが取得できませんでした",
      })
    }
  }

  return (
    <FormProvider {...methods}>
      <FormLayout<IFormValues>
        onSubmit={methods.handleSubmit(onSubmit)}
        questionFields={<QuestionFields />}
        subtitle="ジョーシスにいくらでアウトソーシングできる!?"
        title={
          <>
            情シスアウトソーシング
            <br />
            見積もりシミュレーター
          </>
        }
      />
    </FormProvider>
  )
}

export { BPO }
